import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, TextArea } from '../components/Form';
import { Modal } from '../components/Modal';
import {
  ManualDeduction,
  ManualDeductionCreate,
  useCreateManualDeductionMutation,
  useDeleteManualDeductionMutation,
  useGetManualDeductionsQuery,
  useGetSubscriptionPeriodsQuery,
  useUpdateManualDeductionMutation,
} from '../api/api';

interface ManualDeductionForm {
  subscription_period_id: string;
  quantity: string;
  description: string;
  written_off_at: string;
}

const dateInputValue = (timestamp: number | string): string =>
  new Date(Number(timestamp) * 1000).toISOString().slice(0, 10);

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: { detail?: unknown } }).data;
    if (typeof data?.detail === 'string') return data.detail;
  }
  return 'The request failed. Please try again.';
};

export const ManualDeductions: FC = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const {
    data: deductionsData,
    isLoading,
    isFetching,
    isError,
  } = useGetManualDeductionsQuery({
    page,
    per_page: perPage,
    from: fromDate || undefined,
    to: toDate || undefined,
  });
  const deductions = deductionsData?.documents ?? [];
  const { data: periods, isError: isPeriodsError } = useGetSubscriptionPeriodsQuery();
  const [createDeduction, { isLoading: isCreating }] = useCreateManualDeductionMutation();
  const [updateDeduction, { isLoading: isUpdating }] = useUpdateManualDeductionMutation();
  const [deleteDeduction] = useDeleteManualDeductionMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState<ManualDeduction | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<ManualDeductionForm>({
    subscription_period_id: '',
    quantity: '1',
    description: '',
    written_off_at: '',
  });

  const selectedPeriod = useMemo(
    () => periods?.find((period) => period.id === formData.subscription_period_id),
    [periods, formData.subscription_period_id],
  );

  useEffect(() => {
    if (editingDeduction) {
      setFormData({
        subscription_period_id: editingDeduction.subscription_period_id,
        quantity: String(editingDeduction.quantity),
        description: editingDeduction.description,
        written_off_at: editingDeduction.written_off_at,
      });
    } else {
      setFormData((previous) => ({
        ...previous,
        subscription_period_id: periods?.[0]?.id ?? '',
        quantity: '1',
        description: '',
        written_off_at: periods?.[0] ? dateInputValue(periods[0].period_start) : '',
      }));
    }
    setErrorMessage('');
  }, [editingDeduction, periods, isModalOpen]);

  const openCreate = () => {
    setEditingDeduction(null);
    setIsModalOpen(true);
  };

  const openEdit = (deduction: ManualDeduction) => {
    setEditingDeduction(deduction);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDeduction(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    const quantity = Number(formData.quantity);
    try {
      if (editingDeduction) {
        await updateDeduction({
          id: editingDeduction.id,
          body: {
            quantity,
            description: formData.description,
            written_off_at: formData.written_off_at,
          },
        }).unwrap();
      } else {
        const request: ManualDeductionCreate = {
          subscription_period_id: formData.subscription_period_id,
          quantity,
          description: formData.description,
          written_off_at: formData.written_off_at,
        };
        await createDeduction(request).unwrap();
      }
      setPage(1);
      closeModal();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleDelete = async (deduction: ManualDeduction) => {
    if (!window.confirm(`Delete the ${deduction.quantity}-unit write-off for ${deduction.organization_name}?`)) {
      return;
    }
    setErrorMessage('');
    try {
      await deleteDeduction(deduction.id).unwrap();
      if (deductions.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const periodOptions = (periods ?? []).map((period) => ({
    _id: period.id,
    name: `${period.organization_name} — ${period.subscription_plan_name} (${dateInputValue(period.period_start)} – ${dateInputValue(period.period_end)})`,
  }));
  const total = deductionsData?.total ?? 0;
  const currentPage = deductionsData?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const fromItem = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toItem = Math.min(currentPage * perPage, total);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Manual Deductions</h1>
        <Button type="button" onClick={openCreate} disabled={!periods?.length}>
          Add deduction
        </Button>
      </div>

      {errorMessage && !isModalOpen && (
        <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>
      )}
      <div className="mb-4 grid max-w-xl gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="manual-deductions-from" className="mb-1 block text-sm font-medium text-gray-700">
            From
          </label>
          <input
            id="manual-deductions-from"
            type="date"
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label htmlFor="manual-deductions-to" className="mb-1 block text-sm font-medium text-gray-700">
            To
          </label>
          <input
            id="manual-deductions-to"
            type="date"
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
      {isLoading && <div>Loading...</div>}
      {isError && <div className="mb-4 text-red-600">Failed to load manual deductions.</div>}
      {isPeriodsError && <div className="mb-4 text-red-600">Failed to load subscription periods.</div>}
      {!isLoading && !isError && total === 0 && (
        <div className="rounded bg-white p-6 text-gray-600">No manual deductions found.</div>
      )}

      {deductions.length > 0 && (
        <div className="overflow-hidden rounded-lg bg-white shadow-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Organization / tariff', 'Date', 'Units', 'Description', 'Created by', 'Actions'].map((label) => (
                  <th key={label} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {deductions.map((deduction) => (
                <tr key={deduction.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div>{deduction.organization_name}</div>
                    <div className="text-gray-500">{deduction.subscription_plan_name ?? 'Subscription'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{deduction.written_off_at}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{deduction.quantity}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{deduction.description}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{deduction.created_by_name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    <button className="mr-3 text-blue-600 hover:text-blue-800" onClick={() => openEdit(deduction)}>
                      Edit
                    </button>
                    <button className="text-red-600 hover:text-red-800" onClick={() => void handleDelete(deduction)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isError && total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <label htmlFor="manual-deductions-page-size">Rows per page</label>
            <select
              id="manual-deductions-page-size"
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {PAGE_SIZE_OPTIONS.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
            <span>{isFetching ? 'Updating...' : `Showing ${fromItem}-${toItem} of ${total}`}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((currentPageValue) => Math.max(1, currentPageValue - 1))}
                disabled={currentPage <= 1 || isFetching}
                className="cursor-pointer rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((currentPageValue) => Math.min(totalPages, currentPageValue + 1))}
                disabled={currentPage >= totalPages || isFetching}
                className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingDeduction ? 'Edit manual deduction' : 'Add manual deduction'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingDeduction && (
            <Select
              label="Subscription"
              value={formData.subscription_period_id}
              onChange={(event) => {
                const period = periods?.find((item) => item.id === event.target.value);
                setFormData({
                  ...formData,
                  subscription_period_id: event.target.value,
                  written_off_at: period ? dateInputValue(period.period_start) : '',
                });
              }}
              options={periodOptions}
              required
            />
          )}
          {editingDeduction && selectedPeriod && (
            <div className="mb-4 rounded border border-gray-200 p-3 text-sm text-gray-700">
              {selectedPeriod.organization_name} — {selectedPeriod.subscription_plan_name}
            </div>
          )}
          <Input
            label="Write-off date"
            type="date"
            value={formData.written_off_at}
            min={selectedPeriod ? dateInputValue(selectedPeriod.period_start) : undefined}
            max={selectedPeriod ? dateInputValue(selectedPeriod.period_end) : undefined}
            onChange={(event) => setFormData({ ...formData, written_off_at: event.target.value })}
            required
          />
          <Input
            label="Units"
            type="number"
            min={1}
            step={1}
            value={formData.quantity}
            onChange={(event) => setFormData({ ...formData, quantity: event.target.value })}
            required
          />
          <TextArea
            label="Description"
            rows={4}
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            required
          />
          {errorMessage && <div className="rounded bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>}
          <div className="flex justify-end gap-3">
            <Button type="button" className="bg-gray-500 hover:bg-gray-600" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
